import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { userStorage } from '../utils/userStorage';
import { api as authApi } from '../api/client';
import { tr } from '@quantum/ui';

const DrawingContext = createContext(null);

export function useDrawings() {
  return useContext(DrawingContext);
}

// Alert-touch sound removed: the ONLY sound on the site is the impulse alert
// (utils/sound.js). Kept as a no-op so the alert-trigger call site is unchanged.
const playAlertAlarm = () => {};

// The most recently mounted provider owns Ctrl+Z. The Situations editor nests an
// isolated provider inside the global one, and both listen on window — without an
// owner the shortcut would undo in BOTH stores at once.
let _undoOwner = null;

/**
 * DrawingProvider
 * @param {object|null} initialDrawings  Seed the store with this map instead of
 *   reading global localStorage. Used for ISOLATED nested providers (the
 *   Situations create/detail charts) so their edits never touch the user's real
 *   Screener drawings.
 * @param {boolean} persist  When false, never writes to localStorage (isolated).
 * @param {boolean} showToasts  When false, the nested provider does not render
 *   its own alert-toast container (avoids duplicates).
 */
export function DrawingProvider({ children, initialDrawings = null, persist = true, showToasts = true }) {
  const [drawings, setDrawings] = useState(() => {
    if (initialDrawings) return initialDrawings;
    return userStorage.getJSON('drawings') || {};
  });

  // Undo stack: array of { type: 'add'|'remove', symbol, drawing }
  // Max 30 entries, LIFO. Position drawings (id='active-trading-position') are excluded.
  const undoStackRef = useRef([]);
  const _pushUndo = (entry) => {
    undoStackRef.current.push(entry);
    if (undoStackRef.current.length > 30) undoStackRef.current.shift();
  };

  const [toasts, setToasts] = useState([]);

  // Auto-save drawings to LocalStorage on modifications
  const saveToStorage = useCallback((newDrawings) => {
    if (!persist) return; // isolated provider — keep everything in-memory only
    userStorage.setJSON('drawings', newDrawings);
  }, [persist]);

  const addDrawing = useCallback((symbol, overlay) => {
    if (!symbol) return;
    // A position overlay is the single live trade marker (entry/TP/SL). There must
    // be AT MOST ONE per symbol — never a long and a short overlapping. Detect it
    // either by the well-known id or by the position_* name.
    const isPositionOverlay =
      overlay.id === 'active-trading-position' ||
      overlay.name === 'position_long' || overlay.name === 'position_short';

    // Track in undo stack (exclude ephemeral position overlay)
    if (!isPositionOverlay) {
      _pushUndo({ type: 'add', symbol, drawing: overlay });
    }
    setDrawings(prev => {
      const symDrawings = prev[symbol] || [];
      // Drop the same-id drawing AND, for position overlays, ANY existing position
      // drawing (both directions) so long & short can never coexist on the chart.
      const filtered = symDrawings.filter(d => {
        if (d.id === overlay.id) return false;
        if (isPositionOverlay &&
            (d.id === 'active-trading-position' ||
             d.name === 'position_long' || d.name === 'position_short')) {
          return false;
        }
        return true;
      });
      const updated = {
        ...prev,
        [symbol]: [...filtered, overlay]
      };
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  // Replace a symbol's whole overlay set in one shot — used when applying a chart
  // or situation shared from chat ("your current drawings will be replaced").
  // Keeps any live position overlay so an applied chart never wipes an open trade.
  const replaceDrawings = useCallback((symbol, overlays) => {
    if (!symbol) return;
    setDrawings(prev => {
      const keepPosition = (prev[symbol] || []).filter(d =>
        d.id === 'active-trading-position' || d.name === 'position_long' || d.name === 'position_short');
      const clean = (Array.isArray(overlays) ? overlays : []).filter(d =>
        d && d.id !== 'active-trading-position' && d.name !== 'position_long' && d.name !== 'position_short');
      const updated = { ...prev, [symbol]: [...clean, ...keepPosition] };
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  // `patch` carries non-geometry fields (e.g. a shape's text label). Optional, so
  // every existing 3-arg drag call keeps working unchanged.
  const updateDrawing = useCallback((symbol, id, points, patch = null) => {
    if (!symbol) return;
    setDrawings(prev => {
      const symDrawings = prev[symbol] || [];
      const updatedList = symDrawings.map(d => {
        if (d.id === id) {
          return { ...d, points, ...(patch || {}) };
        }
        return d;
      });
      
      const updated = {
        ...prev,
        [symbol]: updatedList
      };
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  const removeDrawing = useCallback((symbol, id) => {
    if (!symbol) return;
    setDrawings(prev => {
      const symDrawings = prev[symbol] || [];
      const updated = {
        ...prev,
        [symbol]: symDrawings.filter(d => d.id !== id)
      };
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  const broadcastDelete = useCallback((id, symbol, scope = 'global') => {
    if (!id) return;
    setDrawings(prev => {
      // Save deleted drawing to undo stack before removing
      if (id !== 'active-trading-position' && symbol) {
        const drawing = (prev[symbol] || []).find(d => d.id === id);
        if (drawing) _pushUndo({ type: 'remove', symbol, drawing });
      }

      const updated = {};
      if (symbol) {
        // If symbol is specified, only delete from that symbol!
        updated[symbol] = (prev[symbol] || []).filter(d => d.id !== id);
        // keep other symbols unchanged
        Object.keys(prev).forEach(s => {
          if (s !== symbol) updated[s] = prev[s];
        });
      } else {
        // Fallback: delete from all symbols
        Object.keys(prev).forEach(sym => {
          updated[sym] = (prev[sym] || []).filter(d => d.id !== id);
        });
      }
      saveToStorage(updated);
      return updated;
    });
    // Broadcast clean delete event to all active chart panels. `scope` lets the
    // isolated Situations editor delete its own position overlay without the
    // global (Screener) PositionLayer reacting to it.
    const event = new CustomEvent('cyber-drawing-delete', { detail: { id, symbol, scope } });
    window.dispatchEvent(event);
  }, [saveToStorage]);

  // Undo last drawing action (Ctrl+Z)
  const undoDrawing = useCallback(() => {
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    if (entry.type === 'add') {
      // Undo add → remove the drawing silently (no undo stack push)
      setDrawings(prev => {
        const updated = {
          ...prev,
          [entry.symbol]: (prev[entry.symbol] || []).filter(d => d.id !== entry.drawing.id),
        };
        saveToStorage(updated);
        return updated;
      });
    } else if (entry.type === 'remove') {
      // Undo remove → restore the drawing silently
      setDrawings(prev => {
        const updated = {
          ...prev,
          [entry.symbol]: [...(prev[entry.symbol] || []), entry.drawing],
        };
        saveToStorage(updated);
        return updated;
      });
    } else if (entry.type === 'clearAll') {
      // Undo clear-all → put the whole snapshot back. Clearing is the one action
      // that can destroy a lot of work at once, so it must be reversible.
      setDrawings(prev => {
        const updated = { ...prev, [entry.symbol]: entry.drawings };
        saveToStorage(updated);
        return updated;
      });
    }
  }, [saveToStorage]);

  const clearDrawings = useCallback((symbol) => {
    if (!symbol) return;
    // Snapshot BEFORE wiping so Ctrl+Z can bring it all back. Taken outside the
    // updater: a setState updater must stay pure — StrictMode double-invokes it,
    // which would push the same snapshot onto the undo stack twice.
    // Position overlays are excluded from undo everywhere else; stay consistent.
    const restorable = (drawings[symbol] || []).filter(d =>
      d.id !== 'active-trading-position' &&
      d.name !== 'position_long' && d.name !== 'position_short');
    if (restorable.length) _pushUndo({ type: 'clearAll', symbol, drawings: restorable });

    setDrawings(prev => {
      const updated = { ...prev, [symbol]: [] };
      saveToStorage(updated);
      return updated;
    });
    // Broadcast clean clear-all event to all active chart panels
    const event = new CustomEvent('cyber-drawing-clear-all', { detail: { symbol } });
    window.dispatchEvent(event);
  }, [drawings, saveToStorage]);

  // ── Ctrl+Z ──────────────────────────────────────────────────────────────────
  // Bound here, once, so every chart gets it: undoDrawing's doc has promised the
  // shortcut for a long time but nothing was ever listening for it.
  const undoTokenRef = useRef({});   // stable identity for this provider
  useEffect(() => {
    const prev = _undoOwner;
    _undoOwner = undoTokenRef.current;          // newest provider claims the key
    return () => { _undoOwner = prev; };        // hand it back on unmount
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key?.toLowerCase() !== 'z') return;
      if (e.shiftKey) return;                   // leave redo alone
      if (_undoOwner !== undoTokenRef.current) return;   // a nested editor owns it
      // Never steal undo from a field the user is typing in.
      const el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      e.preventDefault();
      undoDrawing();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undoDrawing]);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const triggerAlert = useCallback((symbol, price, id) => {
    playAlertAlarm();

    const toastId = `toast-${Date.now()}-${Math.random()}`;
    const newToast = {
      id: toastId,
      symbol,
      price,
      timestamp: Date.now()
    };

    setToasts(prev => [newToast, ...prev]);

    // Auto-remove toast after 6 seconds
    setTimeout(() => {
      dismissToast(toastId);
    }, 6000);

    // Remove the alert drawing from list with broadcast delete!
    broadcastDelete(id, symbol);

    // Notify Telegram bot (best-effort, fire-and-forget)
    authApi('/bot/alert-hit', {
      method: 'POST',
      body: { symbol, price, direction: null, note: `Alert @ ${price}` },
    }).catch(() => {});
  }, [dismissToast, broadcastDelete]);

  const value = useMemo(() => ({
    drawings,
    addDrawing,
    updateDrawing,
    removeDrawing,
    replaceDrawings,
    broadcastDelete,
    undoDrawing,
    clearDrawings,
    triggerAlert,
    toasts,
    dismissToast,
  }), [drawings, addDrawing, updateDrawing, removeDrawing, replaceDrawings, broadcastDelete, undoDrawing, clearDrawings, triggerAlert, toasts, dismissToast]);

  return (
    <DrawingContext.Provider value={value}>
      {children}

      {/* Visual HUD Toast Container */}
      {showToasts && <div className="cyber-hud-toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className="cyber-hud-toast">
            <span className="cyber-hud-toast-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1 a3 3 0 0 1 3 3 v2.5 l1.2 2.5 H3.8 L5 6.5 V4 a3 3 0 0 1 3-3 Z" stroke="currentColor" strokeWidth="1.1" fill="none"/>
                <path d="M6.4 12 a1.6 1.1 0 0 0 3.2 0" stroke="currentColor" strokeWidth="1.1" fill="none"/>
              </svg>
            </span>
            <div className="cyber-hud-toast-body">
              <div className="cyber-hud-toast-title">{tr('ALERT DETECTED')}</div>
              <div className="cyber-hud-toast-desc">
                <span className="cyber-hud-toast-symbol">{toast.symbol}</span> {tr('crossed alert level at')} <span className="cyber-hud-toast-price">${toast.price.toFixed(5)}</span>
              </div>
            </div>
            <button className="cyber-hud-toast-close" onClick={() => dismissToast(toast.id)}>×</button>
          </div>
        ))}
      </div>}
    </DrawingContext.Provider>
  );
}
